// # Shared Dashboard Footer Component
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../auth/state';
import './DashboardFooter.css';  // Add CSS import
import PropTypes from 'prop-types';  // # Import PropTypes

const DashboardFooter = ({ isDarkMode }) => {
  const { isAdmin, user } = useAuthStore();
  const location = useLocation();
  const [showEmails, setShowEmails] = useState(false);
  const [localDarkMode, setLocalDarkMode] = useState(false);

  const isLandingPage = ['/team', '/products', '/terms', '/faqs', '/privacy'].includes(location.pathname);
  const isLibraryPage = location.pathname.includes('/library/');

  // # Listen for theme changes propagated from DashboardHeader or lib_header
  useEffect(() => {
    const detectTheme = () => {
      // # Update local state if prop is provided
      if (isDarkMode !== undefined) {
        setLocalDarkMode(isDarkMode);
        return;
      }
      
      // # If no prop is provided, try to detect theme from DOM
      // # Check for library-specific dark mode classes first
      const isLibraryDark = document.querySelector('.step1_dark-mode') !== null || 
                           document.querySelector('.step2_dark-mode') !== null ||
                           document.querySelector('.libhdr_dark-mode') !== null;
                           
      if (isLibraryDark) {
        setLocalDarkMode(true);
        return;
      }
      
      // # Then check other common dark mode indicators
      const isDashboardDark = document.querySelector('.usr_dsh_hdr_dark-mode') !== null;
      const isBodyDark = document.body.classList.contains('dark-mode') || 
                        document.documentElement.classList.contains('dark-mode');
                        
      setLocalDarkMode(isDashboardDark || isBodyDark);
    };

    // # Detect theme immediately
    detectTheme();

    // # Setup an interval to check for theme changes for library pages
    // # This handles cases where the theme toggle doesn't propagate through props
    const intervalId = setInterval(() => {
      if (isLibraryPage) {
        detectTheme();
      }
    }, 500);

    // # Also listen for explicit theme change events
    const handleThemeChange = (e) => {
      if (e.detail && e.detail.isDark !== undefined) {
        setLocalDarkMode(e.detail.isDark);
      }
    };

    window.addEventListener('theme_change', handleThemeChange);
    
    return () => {
      window.removeEventListener('theme_change', handleThemeChange);
      clearInterval(intervalId);
    };
  }, [isDarkMode, isLibraryPage, location.pathname]);

  return (
    <footer className={`dsh_fotr_dashboard-footer ${isLandingPage ? 'dsh_fotr_landing-footer' : ''} ${localDarkMode ? 'dsh_fotr_dark-mode' : ''}`}>
      <div className="dsh_fotr_footer-content">
        {/* Left section - Links */}
        <div className="dsh_fotr_footer-section">
          <h4>Quick Links</h4>
          <ul className="dsh_fotr_footer-links">
            <li>
              <i className="fas fa-question-circle"></i>
              <Link to="/faqs">FAQs</Link>
            </li>
            <li>
              <i className="fas fa-file-alt"></i>
              <Link to="/terms">Terms of Use</Link>
            </li>
            <li>
              <i className="fas fa-shield-alt"></i>
              <Link to="/privacy">Privacy Policy</Link>
            </li>
          </ul>
        </div>

        {/* Middle section - Brand */}
        <div className="dsh_fotr_footer-section dsh_fotr_footer-brand-section">
          <div className="dsh_fotr_footer-brand">
            <img 
              src="/icons/TEMPU_Logo.png" 
              alt="TEMP-U Logo" 
              className="dsh_fotr_footer-logo" 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              role="button"
              title="Scroll to top"
            />
            <div className="dsh_fotr_footer-title">
              <h4>{isLandingPage ? 'TEMP-U' : isAdmin ? 'TEMP-U Admin' : 'TEMP-U'}</h4>
              <p>{isLandingPage ? 'The Egyptian Mentorship Program for USMLE' : 
                  isAdmin ? 'Administrative Dashboard' : 
                  'The Egyptian Mentorship Program for USMLE'}</p>
            </div>
          </div>
        </div>

        {/* Right section - Contact */}
        <div className="dsh_fotr_footer-section">
          <h4>Contact Us</h4>
          <div className="dsh_fotr_contact-container">
            {/* Email Toggle Button */}
            <button 
              className={`dsh_fotr_email-toggle ${showEmails ? 'active' : ''}`}
              onClick={() => setShowEmails(!showEmails)}
            >
              <i className="fas fa-envelope"></i>
              <span>Contact Emails</span>
              <i className={`fas fa-chevron-${showEmails ? 'up' : 'down'}`}></i>
            </button>
            
            {/* Email List */}
            <div className={`dsh_fotr_email-list ${showEmails ? 'expanded' : ''}`}>
              {/* Main Email */}
              <div className="dsh_fotr_email-item dsh_fotr_email-main">
                <i className="fas fa-envelope-open-text"></i>
                <a href="mailto:wearetempu@gmail.com">wearetempu@gmail.com</a>
              </div>
              {/* Support Email */}
              <div className="dsh_fotr_email-item">
                <i className="fas fa-headset"></i>
                <a href="mailto:support@wearetempu.com">support@wearetempu.com</a>
              </div>
              {/* Customer Service Email */}
              <div className="dsh_fotr_email-item">
                <i className="fas fa-user-tie"></i>
                <a href="mailto:customer-service@wearetempu.com">customer-service@wearetempu.com</a>
              </div>
            </div>

            {/* Social Links */}
            <div className="dsh_fotr_social-links">
              <a href="https://x.com/We_Are_TEMPU?t=hDMPUGfWyB8RiwiN2eaeWg&s=09" 
                 target="_blank" 
                 rel="noopener noreferrer"
                 className="twitter">
                <i className="fab fa-twitter"></i>
                <span>Twitter</span>
              </a>
              <a href="https://www.facebook.com/share/1VMB4G1KWM/" 
                 target="_blank" 
                 rel="noopener noreferrer"
                 className="facebook">
                <i className="fab fa-facebook"></i>
                <span>Facebook</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="dsh_fotr_footer-bottom">
        <p>&copy; {new Date().getFullYear()} {isLandingPage ? 'TEMP-U' : isAdmin ? 'TEMP-U Admin Panel' : 'TEMP-U'}. All rights reserved.</p>
      </div>
    </footer>
  );
};

// # Add PropTypes at the end before export
DashboardFooter.propTypes = {
  isDarkMode: PropTypes.bool
};

export default DashboardFooter; 